import { useState, useEffect, useRef } from 'react';

/**
 * useState that survives navigating away and back.
 *
 * Drop-in for useState: same [value, setValue] pair, same updater-function support. The value is
 * mirrored into sessionStorage under `key` and read back when the component next mounts.
 *
 * WHY: a page whose list is built by searching (the task Reconciliation list, and every list like it)
 * loses everything the moment you open a record and come back — React unmounts the page, local state
 * goes with it, and you are looking at "Ready to Search" again with your filters blank. sessionStorage
 * rather than localStorage because this is per-tab working state, not a preference: two tabs open on
 * two different tasks must not fight over one saved filter set, and it should not still be there
 * tomorrow.
 *
 * Only put SEARCH CRITERIA and small view state here — filters, sort, page, "have I searched yet".
 * Not result sets: they can be megabytes, sessionStorage is capped (~5MB) and throws when full, and a
 * restored list would be stale anyway. Restore the criteria and re-run the query.
 *
 * Storage failures are non-fatal by design — Safari private mode throws on write, and a filter that
 * cannot be remembered must never take the page down with it.
 *
 * @param {string} key     storage key; scope it per entity (e.g. `recon:${taskId}:status`)
 * @param {*} initialValue used when nothing is stored yet
 */
export default function useSessionState(key, initialValue) {
    const [value, setValue] = useState(() => {
        try {
            const raw = sessionStorage.getItem(key);
            return raw === null ? initialValue : JSON.parse(raw);
        } catch (e) {
            return initialValue;
        }
    });

    // The key is part of the identity of the stored value. If it changes (navigating between two
    // tasks without unmounting), re-read rather than writing this task's value under the other's key.
    const keyRef = useRef(key);
    useEffect(() => {
        if (keyRef.current === key) return;
        keyRef.current = key;
        try {
            const raw = sessionStorage.getItem(key);
            setValue(raw === null ? initialValue : JSON.parse(raw));
        } catch (e) {
            setValue(initialValue);
        }
        // initialValue is deliberately not a dependency: callers pass literals like [] or {}, which are
        // a new reference every render and would re-run this on every render.
    }, [key]);

    useEffect(() => {
        try {
            sessionStorage.setItem(key, JSON.stringify(value));
        } catch (e) {
            // Quota exceeded or storage disabled — the page keeps working, it just will not remember.
        }
    }, [key, value]);

    return [value, setValue];
}

/** Forget everything stored under a prefix — e.g. when a task's records are purged. */
export function clearSessionState(prefix) {
    try {
        Object.keys(sessionStorage)
            .filter((k) => k.startsWith(prefix))
            .forEach((k) => sessionStorage.removeItem(k));
    } catch (e) {
        /* storage unavailable — nothing to clear */
    }
}
